import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import { ChevronRight, ChevronLeft, ShieldCheck } from "lucide-react";

const languages = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिन्दी" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
  { code: "kn", label: "ಕನ್ನಡ" },
];

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    phone: "",
    dob: "",
    gender: "",
    preferred_language: "en",
    consent_data: false,
    consent_recording: false,
    orientation_location: "",
    orientation_datetime: new Date().toISOString().slice(0, 16),
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const phoneOk = /^\+?[0-9\s\-()]{7,20}$/.test(form.phone.trim());
  const canNext =
    (step === 1 && form.name && form.email && form.password.length >= 6 && phoneOk) ||
    (step === 2 && form.consent_data && form.consent_recording) ||
    (step === 3 && form.orientation_location && form.orientation_datetime);

  const submit = async () => {
    setBusy(true);
    const payload = {
      ...form,
      orientation_datetime: new Date(form.orientation_datetime).toISOString(),
    };
    const r = await register(payload);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome to Manodayam");
      nav("/home");
    } else {
      toast.error(r.error);
    }
  };

  return (
    <div className="min-h-screen bg-[color:var(--mano-bg)] px-5 py-8 flex items-center justify-center">
      <div className="w-full max-w-lg bg-white rounded-3xl border border-[color:var(--mano-border)] p-7 lg:p-9">
        <Link to="/" data-testid="register-back-home" className="uppercase-label">← Home</Link>
        <div className="flex items-center gap-1.5 mt-4" aria-hidden>
          {[1, 2, 3].map((n) => (
            <div
              key={n}
              className={`h-1.5 flex-1 rounded-full ${step >= n ? "bg-[color:var(--mano-primary)]" : "bg-[color:var(--mano-border)]"}`}
            />
          ))}
        </div>
        <div className="mt-6 fade-in-up" key={step}>
          {step === 1 && (
            <>
              <h1 className="font-display text-3xl">{t("register.step1")}</h1>
              <p className="text-[color:var(--mano-ink-soft)] mt-2 text-sm">{t("register.step1_sub")}</p>
              <div className="mt-5 space-y-4">
                <Field label={t("register.name")}>
                  <input data-testid="reg-name-input" value={form.name} onChange={(e) => set("name", e.target.value)} className={inp} />
                </Field>
                <Field label={t("register.email")}>
                  <input data-testid="reg-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inp} />
                </Field>
                <Field label={t("register.password")}>
                  <input data-testid="reg-password-input" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className={inp} />
                </Field>
                <Field label={t("register.phone")}>
                  <input
                    data-testid="reg-phone-input"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+91 98765 43210"
                    value={form.phone}
                    onChange={(e) => set("phone", e.target.value)}
                    className={inp}
                  />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t("register.dob")}>
                    <input data-testid="reg-dob-input" type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} className={inp} />
                  </Field>
                  <Field label={t("register.gender")}>
                    <select data-testid="reg-gender-input" value={form.gender} onChange={(e) => set("gender", e.target.value)} className={inp}>
                      <option value="">Select</option>
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="nonbinary">Non-binary</option>
                      <option value="prefer_not">Prefer not to say</option>
                    </select>
                  </Field>
                </div>
                <Field label={t("register.language")}>
                  <select data-testid="reg-lang-input" value={form.preferred_language} onChange={(e) => set("preferred_language", e.target.value)} className={inp}>
                    {languages.map((l) => (
                      <option key={l.code} value={l.code}>{l.label}</option>
                    ))}
                  </select>
                </Field>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <h1 className="font-display text-3xl">{t("register.step2")}</h1>
              <p className="text-[color:var(--mano-ink-soft)] mt-2 text-sm">{t("register.step2_sub")}</p>
              <div className="mt-5 space-y-4">
                <ConsentRow
                  testid="reg-consent-data"
                  checked={form.consent_data}
                  onChange={(v) => set("consent_data", v)}
                  title={t("register.consent_data_title")}
                  body={t("register.consent_data_body")}
                />
                <ConsentRow
                  testid="reg-consent-recording"
                  checked={form.consent_recording}
                  onChange={(v) => set("consent_recording", v)}
                  title={t("register.consent_rec_title")}
                  body={t("register.consent_rec_body")}
                />
                <div className="flex items-start gap-2 text-xs text-[color:var(--mano-ink-soft)] bg-[color:var(--mano-border)]/30 p-3 rounded-xl">
                  <ShieldCheck size={16} className="mt-0.5 shrink-0" />
                  <span>{t("report.crisis")}</span>
                </div>
              </div>
            </>
          )}
          {step === 3 && (
            <>
              <h1 className="font-display text-3xl">{t("register.step3")}</h1>
              <p className="text-[color:var(--mano-ink-soft)] mt-2 text-sm">{t("register.step3_sub")}</p>
              <div className="mt-5 space-y-4">
                <Field label={t("register.orient_where")}>
                  <input data-testid="reg-orient-location" value={form.orientation_location} onChange={(e) => set("orientation_location", e.target.value)} className={inp} placeholder="e.g., Mumbai, at home" />
                </Field>
                <Field label={t("register.orient_when")}>
                  <input data-testid="reg-orient-datetime" type="datetime-local" value={form.orientation_datetime} onChange={(e) => set("orientation_datetime", e.target.value)} className={inp} />
                </Field>
              </div>
            </>
          )}
        </div>
        <div className="mt-8 flex items-center justify-between gap-3">
          {step > 1 ? (
            <button data-testid="reg-back-btn" onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full border border-[color:var(--mano-border)]">
              <ChevronLeft size={16} /> {t("register.back")}
            </button>
          ) : (
            <span />
          )}
          {step < 3 ? (
            <button
              data-testid="reg-next-btn"
              disabled={!canNext}
              onClick={() => setStep(step + 1)}
              className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold disabled:opacity-50 transition-colors"
            >
              {t("register.continue")} <ChevronRight size={16} />
            </button>
          ) : (
            <button
              data-testid="reg-submit-btn"
              disabled={!canNext || busy}
              onClick={submit}
              className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full bg-[color:var(--mano-primary)] hover:bg-[color:var(--mano-primary-hover)] text-white font-semibold disabled:opacity-50 transition-colors"
            >
              {busy ? t("register.creating") : t("register.create")}
            </button>
          )}
        </div>
        <div className="text-sm text-center mt-6 text-[color:var(--mano-ink-soft)]">
          Already registered?{" "}
          <Link to="/login" data-testid="reg-login-link" className="text-[color:var(--mano-primary)] font-semibold">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

const inp =
  "mt-2 w-full h-12 px-4 rounded-xl border border-[color:var(--mano-border)] bg-[color:var(--mano-bg)] focus:outline-none focus:border-[color:var(--mano-primary)]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="uppercase-label">{label}</span>
      {children}
    </label>
  );
}

function ConsentRow({ checked, onChange, title, body, testid }) {
  return (
    <label className={`flex items-start gap-3 p-4 rounded-xl border ${checked ? "border-[color:var(--mano-primary)] bg-[color:var(--mano-primary)]/5" : "border-[color:var(--mano-border)]"}`}>
      <input
        data-testid={testid}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 accent-[color:var(--mano-primary)]"
      />
      <div>
        <div className="font-semibold">{title}</div>
        <div className="text-sm text-[color:var(--mano-ink-soft)] mt-1">{body}</div>
      </div>
    </label>
  );
}
